import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import MobileNav from "@/components/MobileNav";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Upload, Plus, Trash2, Sparkles, Loader2, ImagePlus, X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { formatOdds, parseOddsInput, currencySymbol } from "@/lib/format";

const MARKET_TYPES = [
  { value: "moneyline", label: "Moneyline" },
  { value: "spread", label: "Spread" },
  { value: "total", label: "Total (O/U)" },
  { value: "player_prop", label: "Player Prop" },
  { value: "team_field_goals", label: "Team Field Goals" },
];
const STAT_TYPES = [
  { value: "passingYards", label: "Passing Yards" },
  { value: "rushingYards", label: "Rushing Yards" },
  { value: "receivingYards", label: "Receiving Yards" },
  { value: "receptions", label: "Receptions" },
  { value: "passingTouchdowns", label: "Passing TDs" },
  { value: "rushingTouchdowns", label: "Rushing TDs" },
  { value: "receivingTouchdowns", label: "Receiving TDs" },
  { value: "totalTouchdowns", label: "Anytime / Total TDs" },
];

function emptyLeg() {
  return {
    matchup: "", home_team: "", away_team: "", event_id: "",
    market_type: "spread", pick: "", team_side: "home",
    line: "", over_under: "over", player_name: "", stat_type: "passingYards",
    odds: -110, // canonical American
    odds_display: "1.91", // shown in current format
  };
}

// image tile representation: { file?: File, previewUrl: string, storagePath?: string, uploaded: bool }
export default function AddSlipPage() {
  const { settings } = useSettings();
  const [games, setGames] = useState([]);
  const [tab, setTab] = useState("upload");
  const [images, setImages] = useState([]); // array of image tiles
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stake, setStake] = useState("50");
  const [slipType, setSlipType] = useState("single");
  const [bookmaker, setBookmaker] = useState("");
  const [legs, setLegs] = useState([emptyLeg()]);
  const nav = useNavigate();

  useEffect(() => { api.get("/nfl/games").then(r => setGames(r.data)).catch(() => {}); }, []);

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const tiles = files.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f), uploaded: false }));
    setImages((prev) => [...prev, ...tiles]);
    // Reset the input so same file can be re-added if needed
    e.target.value = "";
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const runExtract = async () => {
    const newTiles = images.filter((t) => !t.uploaded);
    const priorPaths = images.filter((t) => t.uploaded && t.storagePath).map((t) => t.storagePath);
    if (newTiles.length === 0 && priorPaths.length === 0) {
      toast.error("Add at least one screenshot");
      return;
    }
    setExtracting(true);
    try {
      const form = new FormData();
      if (newTiles.length === 0) {
        // Backend requires at least one file; re-upload the first prior tile as a passthrough re-run
        // In practice runExtract is only called after user adds files.
      }
      newTiles.forEach((t) => form.append("files", t.file));
      form.append("prior_paths", JSON.stringify(priorPaths));
      const { data } = await api.post("/slips/ocr-extract", form, { headers: { "Content-Type": "multipart/form-data" } });
      setExtracted(data);
      setBookmaker(data.bookmaker || "");
      setSlipType(data.slip_type || (data.legs?.length > 1 ? "parlay" : "single"));
      setStake(String(data.stake ?? "50"));
      setLegs(data.legs?.length ? data.legs.map(l => ({ ...emptyLeg(), ...l, odds_display: formatOdds(l.odds, settings.odds_format) })) : [emptyLeg()]);
      // Mark all new tiles as uploaded; associate returned paths in order
      const returnedPaths = data.screenshot_paths || [];
      setImages((prev) => {
        // First N returned paths correspond to prior + new — map by order
        // We rebuild image list to keep prior tiles' paths and assign new paths to new tiles
        const priorTiles = prev.filter((t) => t.uploaded);
        const nonUploaded = prev.filter((t) => !t.uploaded);
        // Prior tiles get their existing paths back (in order); new tiles get returned tail
        const newPathTail = returnedPaths.slice(priorTiles.length);
        const updatedNew = nonUploaded.map((t, i) => ({ ...t, uploaded: true, storagePath: newPathTail[i] }));
        return [...priorTiles, ...updatedNew];
      });
      toast.success(`Extracted ${data.legs?.length || 0} leg${data.legs?.length === 1 ? "" : "s"} from ${returnedPaths.length} image${returnedPaths.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "OCR failed");
    } finally { setExtracting(false); }
  };

  const attachEvent = (leg) => {
    if (!leg.home_team || !leg.away_team) return leg;
    const g = games.find(x => x.home_team === leg.home_team.toUpperCase() && x.away_team === leg.away_team.toUpperCase());
    return g ? { ...leg, event_id: g.event_id, matchup: `${leg.away_team} @ ${leg.home_team}` } : leg;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const cleanedLegs = legs.map(l => {
        const enriched = attachEvent(l);
        return {
          ...enriched,
          line: enriched.line === "" ? null : Number(enriched.line),
          odds: Number(enriched.odds || 0),
          matchup: enriched.matchup || `${enriched.away_team} @ ${enriched.home_team}`,
        };
      });
      const screenshotPaths = images.filter((t) => t.uploaded && t.storagePath).map((t) => t.storagePath);
      const { data } = await api.post("/slips/confirm", {
        bookmaker, slip_type: slipType, stake: Number(stake), legs: cleanedLegs,
        screenshot_paths: screenshotPaths,
      });
      toast.success("Slip tracked!");
      nav(`/slip/${data.slip_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const updateLeg = (i, patch) => setLegs(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLeg = (i) => setLegs(ls => ls.filter((_, idx) => idx !== i));
  const addLeg = () => setLegs(ls => [...ls, emptyLeg()]);

  useEffect(() => {
    setSlipType(legs.length > 1 ? "parlay" : "single");
  }, [legs.length]);

  const pendingNewImages = images.some((t) => !t.uploaded);

  return (
    <div className="min-h-screen bg-[#0A0D14] pb-24 md:pb-8">
      <AppHeader />
      <MobileNav />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-white mb-6" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
          Add A New Slip
        </h1>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="add-slip-tabs" className="bg-[#121824] border border-white/10">
            <TabsTrigger data-testid="tab-upload" value="upload" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900">
              <Upload className="h-4 w-4 mr-2" /> Upload Screenshot
            </TabsTrigger>
            <TabsTrigger data-testid="tab-manual" value="manual" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900">
              <Plus className="h-4 w-4 mr-2" /> Manual Entry
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="mt-6">
            <div className="rounded-xl bg-[#121824] border border-white/10 p-4 sm:p-6 space-y-4">
              {images.length === 0 ? (
                <label
                  htmlFor="ocr-file"
                  data-testid="ocr-dropzone"
                  className="border-2 border-dashed border-white/15 rounded-lg p-10 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-emerald-500/50 transition-colors"
                >
                  <Upload className="h-10 w-10 text-slate-400" />
                  <div className="text-white font-bold uppercase tracking-wide" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                    Drop bet slip screenshots
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest">Multiple images OK · PNG · JPG · WEBP</div>
                  <input id="ocr-file" data-testid="ocr-upload-input" type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={onFiles} />
                </label>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {images.map((tile, i) => (
                      <div key={i} data-testid={`image-tile-${i}`} className="relative group aspect-[3/4] rounded-lg overflow-hidden border border-white/10 bg-black/40">
                        <img src={tile.previewUrl} alt="" className="w-full h-full object-cover" />
                        {!tile.uploaded && (
                          <div className="absolute top-1 left-1 text-[10px] uppercase tracking-widest bg-amber-500/90 text-slate-900 font-bold px-1.5 py-0.5 rounded">New</div>
                        )}
                        {tile.uploaded && (
                          <div className="absolute top-1 left-1 text-[10px] uppercase tracking-widest bg-emerald-500/90 text-slate-900 font-bold px-1.5 py-0.5 rounded">✓ Scanned</div>
                        )}
                        <button
                          data-testid={`remove-image-${i}`}
                          onClick={() => removeImage(i)}
                          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 hover:bg-red-500 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <label
                      htmlFor="ocr-file-more"
                      data-testid="add-more-images"
                      className="aspect-[3/4] rounded-lg border-2 border-dashed border-white/15 hover:border-emerald-500/50 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-white cursor-pointer transition-colors"
                    >
                      <ImagePlus className="h-6 w-6" />
                      <span className="text-[10px] uppercase tracking-widest font-semibold">Add more</span>
                      <input id="ocr-file-more" type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={onFiles} />
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/10">
                    <Button
                      data-testid="run-ocr-button"
                      onClick={runExtract}
                      disabled={extracting || images.length === 0}
                      className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold uppercase tracking-wider"
                    >
                      {extracting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning...</> : (
                        extracted ? <><Sparkles className="h-4 w-4 mr-2" /> Re-extract with all images</> : <><Sparkles className="h-4 w-4 mr-2" /> Extract with AI</>
                      )}
                    </Button>
                    {pendingNewImages && extracted && (
                      <span className="text-xs text-amber-300 uppercase tracking-widest">New images added — re-extract to merge them</span>
                    )}
                    {extracted && !pendingNewImages && (
                      <span className="text-xs text-emerald-300 uppercase tracking-widest">
                        ✓ {legs.length} unique leg{legs.length === 1 ? "" : "s"} from {images.length} image{images.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="manual" className="mt-6" />
        </Tabs>

        {(tab === "manual" || extracted) && (
          <div className="mt-6 rounded-xl bg-[#121824] border border-white/10 p-4 sm:p-6 space-y-5">
            <h2 className="text-xl font-bold uppercase tracking-wide text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Review & Confirm
            </h2>
            {extracted && (
              <p className="text-xs text-slate-400">
                Combined from {images.length} screenshot{images.length === 1 ? "" : "s"}. Edit or remove any leg below before saving.
              </p>
            )}
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">Bookmaker</Label>
                <Input data-testid="bookmaker-input" value={bookmaker} onChange={e => setBookmaker(e.target.value)} placeholder="FanDuel" className="bg-[#0E1420] border-white/10 text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">Stake ({currencySymbol(settings.currency)})</Label>
                <Input data-testid="stake-input" type="number" value={stake} onChange={e => setStake(e.target.value)} className="bg-[#0E1420] border-white/10 text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">Type</Label>
                <div className="mt-1 px-3 py-2 rounded-md bg-[#0E1420] border border-white/10 text-white text-sm">{slipType}</div>
              </div>
            </div>

            <div className="space-y-3">
              {legs.map((leg, i) => (
                <div key={i} className="rounded-lg border border-white/10 p-3 bg-[#0E1420]" data-testid={`manual-leg-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
                      <span>Leg {i + 1}</span>
                      {extracted && <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 text-[10px]">AI extracted</span>}
                    </div>
                    <Button size="icon" variant="ghost" data-testid={`remove-leg-${i}`} onClick={() => removeLeg(i)} disabled={legs.length === 1}>
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Game</Label>
                      <Select value={leg.event_id || ""} onValueChange={v => {
                        const g = games.find(x => x.event_id === v);
                        if (g) updateLeg(i, { event_id: g.event_id, home_team: g.home_team, away_team: g.away_team, matchup: `${g.away_team} @ ${g.home_team}` });
                      }}>
                        <SelectTrigger data-testid={`leg-game-${i}`} className="bg-[#121824] border-white/10 text-white mt-1"><SelectValue placeholder={leg.matchup || "Select game"} /></SelectTrigger>
                        <SelectContent className="bg-[#121824] border-white/10 text-white">
                          {games.length === 0 && <SelectItem value="none" disabled>No live games</SelectItem>}
                          {games.map(g => <SelectItem key={g.event_id} value={g.event_id}>{g.short_name} · {g.detail}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Market</Label>
                      <Select value={leg.market_type} onValueChange={v => updateLeg(i, { market_type: v })}>
                        <SelectTrigger data-testid={`leg-market-${i}`} className="bg-[#121824] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121824] border-white/10 text-white">
                          {MARKET_TYPES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {(leg.market_type === "moneyline" || leg.market_type === "spread" || leg.market_type === "team_field_goals") && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">Team side</Label>
                        <Select value={leg.team_side || "home"} onValueChange={v => updateLeg(i, { team_side: v })}>
                          <SelectTrigger data-testid={`leg-side-${i}`} className="bg-[#121824] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#121824] border-white/10 text-white">
                            <SelectItem value="home">{leg.home_team || "Home"}</SelectItem>
                            <SelectItem value="away">{leg.away_team || "Away"}</SelectItem>
                            {leg.market_type === "team_field_goals" && <SelectItem value="both">Each team (both)</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {leg.market_type !== "moneyline" && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">Line</Label>
                        <Input data-testid={`leg-line-${i}`} type="number" step="0.5" value={leg.line ?? ""} onChange={e => updateLeg(i, { line: e.target.value })} className="bg-[#121824] border-white/10 text-white mt-1" />
                      </div>
                    )}
                    {(leg.market_type === "total" || leg.market_type === "player_prop" || leg.market_type === "team_field_goals") && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">Over/Under</Label>
                        <Select value={leg.over_under || "over"} onValueChange={v => updateLeg(i, { over_under: v })}>
                          <SelectTrigger data-testid={`leg-ou-${i}`} className="bg-[#121824] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#121824] border-white/10 text-white">
                            <SelectItem value="over">Over</SelectItem>
                            <SelectItem value="under">Under</SelectItem>
                            {leg.market_type === "team_field_goals" && <SelectItem value="each">Each team (≥1)</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {leg.market_type === "player_prop" && (
                      <>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Player</Label>
                          <Input data-testid={`leg-player-${i}`} value={leg.player_name || ""} onChange={e => updateLeg(i, { player_name: e.target.value })} placeholder="Patrick Mahomes" className="bg-[#121824] border-white/10 text-white mt-1" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Stat</Label>
                          <Select value={leg.stat_type || "passingYards"} onValueChange={v => updateLeg(i, { stat_type: v })}>
                            <SelectTrigger data-testid={`leg-stat-${i}`} className="bg-[#121824] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#121824] border-white/10 text-white">
                              {STAT_TYPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Odds ({settings.odds_format})</Label>
                      <Input
                        data-testid={`leg-odds-${i}`}
                        value={leg.odds_display ?? formatOdds(leg.odds, settings.odds_format)}
                        onChange={(e) => {
                          const display = e.target.value;
                          const american = parseOddsInput(display, settings.odds_format);
                          updateLeg(i, { odds_display: display, odds: american });
                        }}
                        placeholder={settings.odds_format === "decimal" ? "1.91" : settings.odds_format === "fractional" ? "10/11" : "-110"}
                        className="bg-[#121824] border-white/10 text-white mt-1"
                      />
                      <div className="text-[10px] text-slate-500 mt-1 font-mono">Stored as American: {leg.odds > 0 ? `+${leg.odds}` : leg.odds}</div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Selection description</Label>
                      <Input data-testid={`leg-pick-${i}`} value={leg.pick} onChange={e => updateLeg(i, { pick: e.target.value })} placeholder="Chiefs -3.5" className="bg-[#121824] border-white/10 text-white mt-1" />
                    </div>
                  </div>
                </div>
              ))}
              <Button data-testid="add-leg-button" variant="outline" onClick={addLeg} className="border-white/10 text-slate-200 bg-white/5 hover:bg-white/10">
                <Plus className="h-4 w-4 mr-2" /> Add another leg
              </Button>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <Button data-testid="cancel-button" variant="ghost" onClick={() => nav("/dashboard")} className="text-slate-300">Cancel</Button>
              <Button
                data-testid="manual-bet-submit"
                onClick={submit}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold uppercase tracking-wider"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Confirm & Track
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
